import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/auth/auth-context";

const signInSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters")
});

type SignInForm = z.infer<typeof signInSchema>;

interface SignInProps {
  onSuccess?: () => void;
  onSwitchToSignUp?: () => void;
  onForgotPassword?: () => void;
}

export const SignIn = ({ onSuccess, onSwitchToSignUp, onForgotPassword }: SignInProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { signInWithEmailAndPassword, signInWithGoogle } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SignInForm>({
    resolver: zodResolver(signInSchema)
  });

  const handleEmailSignIn = async (data: SignInForm) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await signInWithEmailAndPassword(data.email, data.password);
      setSuccess("Signed in successfully!");
      onSuccess?.();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Sign in failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await signInWithGoogle();
      setSuccess("Signed in successfully!");
      onSuccess?.();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Google sign in failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 text-center">
        <h1 className="text-xl font-semibold text-gray-900">
          <b>Tigim HQ</b> Sign In
        </h1>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <form
        autoComplete="off"
        onSubmit={handleSubmit(handleEmailSignIn)}
        className="space-y-3"
        aria-label="Sign in form"
      >
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            className="w-full h-9"
            required
            disabled={isLoading}
            aria-describedby="email-description"
          />
          <div id="email-description" className="sr-only">
            Enter your email address
          </div>
          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <Input
            id="password"
            type="password"
            {...register("password")}
            className="w-full h-9"
            required
            disabled={isLoading}
            autoComplete="new-password"
            aria-describedby="password-description"
          />
          <div id="password-description" className="sr-only">
            Add the password of your account
          </div>
          {errors.password && (
            <p className="text-sm text-red-600 mt-1">{errors.password.message}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full h-10 text-base bg-purple-600 hover:bg-[#0f172a] text-white"
          disabled={isLoading}
          aria-label="Sign in with provided information"
        >
          {isLoading ? "Signing in..." : "Sign In"}
        </Button>

        <div className="relative" aria-label="Alternative signin options">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleSignIn}
          className="w-full h-10 text-base border-gray-300 hover:bg-gray-50"
          disabled={isLoading}
          aria-label="Sign in with Google account"
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {isLoading ? "Signing in..." : "Sign in with Google"}
        </Button>
      </form>

      <div className="text-center mt-4 flex flex-col items-center gap-2">
        <Button variant="link" onClick={onSwitchToSignUp} disabled={isLoading} className="h-5">
          Don't have an account? Sign Up
        </Button>

        <Button variant="link" onClick={onForgotPassword} disabled={isLoading} className="h-5">
          Forgot Password?
        </Button>
      </div>
    </div>
  );
};
